import type { HospitalCountersResponse, HospitalResultsQuery, HospitalResultsResponse } from "./types";

type AuthPayload = {
  access_token?: string;
  iin?: string;
};

function getApiBase(): string {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return String(env?.VITE_API_BASE || "").replace(/\/+$/, "");
}

function buildUrl(path: string, query?: Record<string, string | number | undefined | null>): string {
  const base = getApiBase();
  let normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (/\/api$/i.test(base) && /^\/api\//i.test(normalizedPath)) {
    normalizedPath = normalizedPath.replace(/^\/api/i, "");
  }
  const full = new URL(`${base}${normalizedPath}`, window.location.origin);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") return;
      full.searchParams.set(key, String(value));
    });
  }
  return full.toString();
}

function getAuthPayload(): AuthPayload | null {
  try {
    const raw = localStorage.getItem("auth_payload");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthPayload;
    if (!parsed?.access_token) return null;
    return parsed;
  } catch {
    return null;
  }
}

async function parseJsonOrThrow<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const data = await response.json();
      if (data && typeof data === "object" && "detail" in data) {
        message = String((data as { detail?: unknown }).detail ?? message);
      }
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return (await response.json()) as T;
}

export async function fetchHospitalMisDocUrl(docId: string, misId: string): Promise<string> {
  const auth = getAuthPayload();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (auth?.access_token) headers.Authorization = `Bearer ${auth.access_token}`;
  if (auth?.iin) headers["X-User-IIN"] = auth.iin;
  const response = await fetch(
    buildUrl("/api/v1/analytics/mis-doc-url-hospital", { docId, orgId: misId }),
    { headers, credentials: "omit" },
  );
  const data = await parseJsonOrThrow<{ url?: string }>(response);
  const url = String(data?.url || "").trim();
  if (!url) throw new Error("МИС не вернула ссылку");
  return url;
}

export async function fetchHospitalResults(query: HospitalResultsQuery): Promise<HospitalResultsResponse> {
  const auth = getAuthPayload();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (auth?.access_token) headers.Authorization = `Bearer ${auth.access_token}`;
  if (auth?.iin) headers["X-User-IIN"] = auth.iin;

  const response = await fetch(
    buildUrl("/api/v1/hospital/results", {
      mis_id: query.mis_id,
      department: query.department,
      doctor: query.doctor,
      hospital_type: query.hospital_type,
      from_date: query.from_date,
      to_date: query.to_date,
      limit: query.limit ?? 200,
      offset: query.offset ?? 0,
    }),
    {
      method: "GET",
      credentials: "omit",
      headers,
    },
  );

  return parseJsonOrThrow<HospitalResultsResponse>(response);
}

export async function fetchHospitalCounters(query: HospitalResultsQuery): Promise<HospitalCountersResponse> {
  const auth = getAuthPayload();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (auth?.access_token) headers.Authorization = `Bearer ${auth.access_token}`;
  if (auth?.iin) headers["X-User-IIN"] = auth.iin;

  const response = await fetch(
    buildUrl("/api/v1/hospital/counters", {
      mis_id: query.mis_id,
      department: query.department,
      doctor: query.doctor,
      hospital_type: query.hospital_type,
      from_date: query.from_date,
      to_date: query.to_date,
    }),
    {
      method: "GET",
      credentials: "omit",
      headers,
    },
  );

  return parseJsonOrThrow<HospitalCountersResponse>(response);
}
